import { useEffect, useState } from 'react';
import { api } from '@/shared/api/client';

// Mirrors BE com.qssi.ph10.features.about.AboutController.AboutResponse.
type ScopeRef = { id: string; priority: number };
type PoolUsage = { max: number; used: number };
type AppInfo = {
  name: string;
  version: string;
  profile: string;
  dbServer: string;
  dbName: string;
};
type LicenseSection = {
  company: string | null;
  expireDate: string | null;
  crt: PoolUsage;
  rfReal: PoolUsage;
  rfSim: PoolUsage;
  message: string | null;
};
type AccessSection = {
  userId: string;
  currentWarehouse: string | null;
  currentOwner: string | null;
  accessibleWarehouses: ScopeRef[];
  accessibleOwners: ScopeRef[];
  groups: string[];
  moduleCount: number;
};
type AboutResponse = { app: AppInfo; license: LicenseSection; access: AccessSection };

type Tab = 'app' | 'license' | 'access';

/**
 * About modal -- slice-316. Three tabs (Application / License / Your Access) backed by a
 * single GET /api/v1/about call. Opened from the ProfileMenu.
 */
export function AboutModal({ onClose }: { onClose: () => void }) {
  const [data, setData] = useState<AboutResponse | null>(null);
  const [err, setErr] = useState<string | null>(null);
  const [tab, setTab] = useState<Tab>('app');

  useEffect(() => {
    let cancelled = false;
    api<AboutResponse>('/api/v1/about')
      .then((d) => {
        if (!cancelled) setData(d);
      })
      .catch((e: unknown) => {
        if (!cancelled) setErr(e instanceof Error ? e.message : 'Failed to load About.');
      });
    return () => {
      cancelled = true;
    };
  }, []);

  // ESC closes
  useEffect(() => {
    function onKey(e: KeyboardEvent) {
      if (e.key === 'Escape') onClose();
    }
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [onClose]);

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/40"
      onMouseDown={(e) => {
        if (e.target === e.currentTarget) onClose();
      }}
    >
      <div className="w-full max-w-2xl rounded-lg border border-slate-200 bg-white shadow-2xl">
        <header className="flex items-center justify-between border-b border-phblue-900 bg-phblue-700 px-4 py-2 text-white">
          <h2 className="text-base font-semibold">About PowerHouse 10</h2>
          <button
            type="button"
            onClick={onClose}
            className="rounded p-1 text-phblue-200 hover:bg-phblue-600 hover:text-white"
            aria-label="Close"
          >
            ✕
          </button>
        </header>

        <nav className="flex border-b border-slate-200 bg-slate-50 text-sm">
          <TabBtn label="Application" active={tab === 'app'} onClick={() => setTab('app')} />
          <TabBtn label="License" active={tab === 'license'} onClick={() => setTab('license')} />
          <TabBtn label="Your Access" active={tab === 'access'} onClick={() => setTab('access')} />
        </nav>

        <div className="min-h-[320px] p-5 text-sm">
          {err && (
            <div className="rounded border border-red-300 bg-red-50 p-3 text-xs text-red-700">
              {err}
            </div>
          )}
          {!data && !err && <div className="text-slate-500">Loading…</div>}
          {data && tab === 'app' && <AppTab info={data.app} />}
          {data && tab === 'license' && <LicenseTab info={data.license} />}
          {data && tab === 'access' && <AccessTab info={data.access} />}
        </div>
      </div>
    </div>
  );
}

function TabBtn({ label, active, onClick }: { label: string; active: boolean; onClick: () => void }) {
  return (
    <button
      type="button"
      onClick={onClick}
      className={
        'border-b-2 px-4 py-2 -mb-px ' +
        (active
          ? 'border-phblue-600 font-semibold text-phblue-800'
          : 'border-transparent text-slate-600 hover:text-slate-900')
      }
    >
      {label}
    </button>
  );
}

function AppTab({ info }: { info: AppInfo }) {
  return (
    <dl className="grid grid-cols-[max-content_1fr] gap-x-6 gap-y-2">
      <Dt>Application</Dt>
      <Dd>{info.name}</Dd>
      <Dt>Version</Dt>
      <Dd className="font-mono">{info.version}</Dd>
      <Dt>Profile</Dt>
      <Dd className="font-mono">{info.profile || '(default)'}</Dd>
      <Dt>Backend</Dt>
      <Dd>Spring Boot · Java 21</Dd>
      <Dt>Frontend</Dt>
      <Dd>React 18 · Vite · AG-Grid Enterprise v32</Dd>
    </dl>
  );
}

function LicenseTab({ info }: { info: LicenseSection }) {
  if (info.message) {
    return <div className="text-xs text-amber-700">{info.message}</div>;
  }
  return (
    <dl className="grid grid-cols-[max-content_1fr] gap-x-6 gap-y-2">
      <Dt>Licensed to</Dt>
      <Dd>{info.company || '(unspecified)'}</Dd>
      <Dt>Expires</Dt>
      <Dd>{info.expireDate ? info.expireDate : 'never'}</Dd>
      <Dt>CRT sessions</Dt>
      <Dd>
        <Bar used={info.crt.used} max={info.crt.max} />
      </Dd>
      <Dt>RF (real)</Dt>
      <Dd>
        <Bar used={info.rfReal.used} max={info.rfReal.max} />
      </Dd>
      <Dt>RF (sim)</Dt>
      <Dd>
        <Bar used={info.rfSim.used} max={info.rfSim.max} />
      </Dd>
    </dl>
  );
}

function Bar({ used, max }: { used: number; max: number }) {
  const pct = max > 0 ? Math.min(100, Math.round((used / max) * 100)) : 0;
  const color = pct >= 90 ? 'bg-red-500' : pct >= 75 ? 'bg-amber-500' : 'bg-phblue-500';
  return (
    <div className="flex items-center gap-3">
      <div className="h-2 w-40 overflow-hidden rounded bg-slate-200">
        <div className={'h-full ' + color} style={{ width: `${pct}%` }} />
      </div>
      <span className="font-mono text-xs text-slate-600">{used} / {max}</span>
    </div>
  );
}

function AccessTab({ info }: { info: AccessSection }) {
  return (
    <dl className="grid grid-cols-[max-content_1fr] gap-x-6 gap-y-2">
      <Dt>User</Dt>
      <Dd className="font-mono">{info.userId}</Dd>
      <Dt>Current scope</Dt>
      <Dd className="font-mono">
        {info.currentWarehouse ?? '—'} / {info.currentOwner ?? '—'}
      </Dd>
      <Dt>Warehouses ({info.accessibleWarehouses.length})</Dt>
      <Dd>
        <ChipList items={info.accessibleWarehouses.map((w) => w.id)} highlight={info.currentWarehouse} />
      </Dd>
      <Dt>Owners ({info.accessibleOwners.length})</Dt>
      <Dd>
        <ChipList items={info.accessibleOwners.map((o) => o.id)} highlight={info.currentOwner} />
      </Dd>
      <Dt>Security groups ({info.groups.length})</Dt>
      <Dd>
        <ChipList items={info.groups} highlight={null} />
      </Dd>
      <Dt>Module entitlements</Dt>
      <Dd>
        <span className="font-mono">{info.moduleCount}</span>
        <span className="ml-2 text-xs text-slate-500">
          (controls nav visibility and per-action access)
        </span>
      </Dd>
    </dl>
  );
}

function ChipList({ items, highlight }: { items: string[]; highlight: string | null }) {
  if (items.length === 0) return <span className="text-xs text-slate-400">(none)</span>;
  return (
    <div className="flex flex-wrap gap-1">
      {items.map((s) => (
        <span
          key={s}
          className={
            'rounded border px-1.5 py-0.5 font-mono text-[11px] ' +
            (s === highlight
              ? 'border-phblue-500 bg-phblue-50 text-phblue-800'
              : 'border-slate-300 bg-slate-50 text-slate-700')
          }
        >
          {s}
        </span>
      ))}
    </div>
  );
}

function Dt({ children }: { children: React.ReactNode }) {
  return <dt className="text-xs uppercase tracking-wide text-slate-500">{children}</dt>;
}
function Dd({ children, className }: { children: React.ReactNode; className?: string }) {
  return <dd className={'text-slate-800 ' + (className ?? '')}>{children}</dd>;
}
